import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, ListGroup } from 'react-bootstrap';
import { PaginasService } from '../../../../service/paginas.service';
import { Pagina } from '../../../../service/types/cursos.types';
import PaginaForm from './PaginaForm';
import TestBuilder from './TestBuilder';

interface PaginaManagerProps {
  moduloId: string;
}

const TIPO_LABEL: Record<string, string> = {
  video: 'Video',
  documento: 'Documento',
  imagen: 'Imagen',
  youtube: 'YouTube',
  descripcion: 'Descripción',
  test: 'Test',
};

export default function PaginaManager({ moduloId }: PaginaManagerProps) {
  const [paginas, setPaginas] = useState<Pagina[]>([]);
  const [editing, setEditing] = useState<Pagina | 'new' | null>(null);
  const [testingPaginaId, setTestingPaginaId] = useState<string | null>(null);

  const load = async () => {
    const data = await PaginasService.findAllByModulo(moduloId);
    setPaginas(data);
  };

  useEffect(() => {
    load();
    setEditing(null);
    setTestingPaginaId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloId]);

  const handleDelete = async (pagina: Pagina) => {
    if (!window.confirm(`¿Eliminar la página "${pagina.titulo}"?`)) return;
    await PaginasService.remove(pagina.id);
    await load();
  };

  const moveOrder = async (pagina: Pagina, direction: -1 | 1) => {
    const sorted = [...paginas].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((p) => p.id === pagina.id);
    const swapWith = sorted[idx + direction];
    if (!swapWith) return;
    await Promise.all([
      PaginasService.update(pagina.id, { orden: swapWith.orden }),
      PaginasService.update(swapWith.id, { orden: pagina.orden }),
    ]);
    await load();
  };

  const sorted = [...paginas].sort((a, b) => a.orden - b.orden);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Páginas del módulo</span>
        <Button size="sm" variant="success" onClick={() => setEditing('new')}>
          + Nueva página
        </Button>
      </Card.Header>
      <Card.Body>
        {editing === 'new' && (
          <PaginaForm
            moduloId={moduloId}
            onSaved={() => {
              setEditing(null);
              load();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
        <ListGroup>
          {sorted.map((pagina, idx) => (
            <ListGroup.Item key={pagina.id}>
              {editing !== 'new' && editing?.id === pagina.id ? (
                <PaginaForm
                  moduloId={moduloId}
                  initialValue={pagina}
                  onSaved={() => {
                    setEditing(null);
                    load();
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Badge bg="secondary" className="me-2">
                      {pagina.orden}
                    </Badge>
                    <strong>{pagina.titulo}</strong>{' '}
                    <span className="text-muted">({TIPO_LABEL[pagina.tipo] ?? pagina.tipo})</span>
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" disabled={idx === 0} onClick={() => moveOrder(pagina, -1)}>
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={idx === sorted.length - 1}
                      onClick={() => moveOrder(pagina, 1)}
                    >
                      ↓
                    </Button>
                    {pagina.tipo === 'test' && (
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => setTestingPaginaId(testingPaginaId === pagina.id ? null : pagina.id)}
                      >
                        {testingPaginaId === pagina.id ? 'Ocultar preguntas' : 'Editar preguntas'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline-primary" onClick={() => setEditing(pagina)}>
                      Editar
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(pagina)}>
                      Eliminar
                    </Button>
                  </div>
                </div>
              )}
              {testingPaginaId === pagina.id && (
                <div className="mt-3">
                  <TestBuilder paginaId={pagina.id} onClose={() => setTestingPaginaId(null)} />
                </div>
              )}
            </ListGroup.Item>
          ))}
          {sorted.length === 0 && editing !== 'new' && (
            <ListGroup.Item className="text-center text-muted">No hay páginas en este módulo</ListGroup.Item>
          )}
        </ListGroup>
      </Card.Body>
    </Card>
  );
}
